import { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch, BASE_URL } from '../config/api';
import VisitorCheckInModal from '../components/VisitorCheckInModal';
import {
  RESTRICTION_SEVERITIES,
  VISITOR_TYPES,
  formatCalendarDate,
  formatVisitWhen,
  severityLabel,
  visitReasonLabel,
  visitorTypeLabel,
} from '../lib/visitorForm';
import { acknowledgeVisitorScan, prepareIdScanner, readIdScannerConnection, subscribeIdScanner } from '../lib/idScanner';
import { readBadgePrinterStatus } from '../lib/dymoBadgePrinter';
import { printVisitorBadge } from '../lib/visitorHardware';
import { cropLicensePortrait, orientIdCard } from '../lib/idScanTestUpload';

function IdCardIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <circle cx="9" cy="11" r="2" />
      <path d="M6.6 16.2c.5-1.3 1.6-2 2.4-2s1.9.7 2.4 2" />
      <path d="M14 10h4M14 13h3.5" />
    </svg>
  );
}

function todayKey() {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

function emptyRestriction() {
  return {
    id: null,
    full_name: '',
    reason: '',
    severity: 'alert_only',
    expires_on: '',
    visitor_id: null,
  };
}

async function portraitForReprint(visit, token, schoolId) {
  if (!visit?.has_id_image || !visit.visitor_id) return '';
  const response = await fetch(`${BASE_URL}/visitors/people/${visit.visitor_id}/id-image?school_id=${schoolId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) return '';
  const blob = await response.blob();
  const dataUrl = await new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => resolve('');
    reader.readAsDataURL(blob);
  });
  if (!dataUrl.startsWith('data:image/')) return '';
  return cropLicensePortrait(dataUrl);
}

async function fetchPrivateImage(path, token) {
  const response = await fetch(`${BASE_URL}${path}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) return '';
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  try {
    const oriented = await orientIdCard(url);
    if (oriented && oriented !== url) {
      URL.revokeObjectURL(url);
      return oriented;
    }
  } catch (error) {
    return url;
  }
  return url;
}

export default function VisitorsPage({ token, schoolId, selectVisitId = null }) {
  const [tab, setTab] = useState('campus');
  const [summary, setSummary] = useState({ on_campus: 0, today_visits: 0, school: { name: 'School' } });
  const [visits, setVisits] = useState([]);
  const [restrictions, setRestrictions] = useState([]);
  const [restrictionImages, setRestrictionImages] = useState({});
  const [search, setSearch] = useState('');
  const [date, setDate] = useState(todayKey);
  const [visitorType, setVisitorType] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [checkInOpen, setCheckInOpen] = useState(false);
  const [checkInScanId, setCheckInScanId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailImageUrl, setDetailImageUrl] = useState('');
  const [checkoutVisit, setCheckoutVisit] = useState(null);
  const [printingId, setPrintingId] = useState(null);
  const [checkingOut, setCheckingOut] = useState(false);
  const [scannerConnected, setScannerConnected] = useState(null);
  const [printerConnected, setPrinterConnected] = useState(null);
  const [testingPrint, setTestingPrint] = useState(false);
  const [restrictionForm, setRestrictionForm] = useState(emptyRestriction);
  const [restrictionOpen, setRestrictionOpen] = useState(false);
  const [idPreview, setIdPreview] = useState(null);
  const [idLoadingId, setIdLoadingId] = useState(null);
  const [savingRestriction, setSavingRestriction] = useState(false);
  const checkInButtonRef = useRef(null);
  const imageUrlRef = useRef('');
  const openedScanId = useRef('');

  const loadSummary = useCallback(async () => {
    const response = await apiFetch(`/visitors/summary?school_id=${schoolId}`, {}, token);
    setSummary(response.data);
  }, [schoolId, token]);

  const loadVisits = useCallback(async () => {
    const params = new URLSearchParams({ school_id: String(schoolId) });
    if (search.trim()) params.set('q', search.trim());
    if (visitorType) params.set('visitor_type', visitorType);
    if (tab === 'campus') {
      params.set('status', 'on_campus');
    } else {
      if (status) params.set('status', status);
      if (date) params.set('date', date);
    }
    const response = await apiFetch(`/visitors/visits?${params}`, {}, token);
    setVisits(response.data || []);
  }, [date, schoolId, search, status, tab, token, visitorType]);

  const loadRestrictions = useCallback(async () => {
    const response = await apiFetch(`/visitors/restrictions?school_id=${schoolId}`, {}, token);
    setRestrictions(response.data || []);
  }, [schoolId, token]);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      await loadSummary();
      if (tab === 'flagged') await loadRestrictions();
      else await loadVisits();
    } catch (loadError) {
      setError(loadError.message || 'Visitors could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [loadRestrictions, loadSummary, loadVisits, tab]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  function closeIdPreview() {
    setIdPreview((current) => {
      const keep = new Set([detailImageUrl, ...Object.values(restrictionImages)].filter(Boolean));
      if (current?.src?.startsWith('blob:') && !keep.has(current.src)) URL.revokeObjectURL(current.src);
      return null;
    });
  }

  useEffect(() => {
    if (!idPreview) return undefined;
    function onKey(event) {
      if (event.key === 'Escape') closeIdPreview();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [idPreview, detailImageUrl, restrictionImages]);

  useEffect(() => {
    let cancelled = false;
    const created = [];
    const flagged = restrictions.filter((item) => item.has_id_image && item.visitor_id);
    if (!flagged.length) {
      setRestrictionImages({});
      return undefined;
    }
    Promise.all(flagged.map(async (item) => {
      const url = await fetchPrivateImage(
        `/visitors/people/${item.visitor_id}/id-image?school_id=${schoolId}`,
        token,
      );
      if (url) created.push(url);
      return [item.id, url];
    })).then((pairs) => {
      if (cancelled) {
        created.forEach((url) => URL.revokeObjectURL(url));
        return;
      }
      setRestrictionImages(Object.fromEntries(pairs.filter(([, url]) => url)));
    });
    return () => {
      cancelled = true;
      created.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [restrictions, schoolId, token]);

  useEffect(() => {
    let cancelled = false;
    async function refreshDevices() {
      const [scanner, printer] = await Promise.all([
        readIdScannerConnection(),
        readBadgePrinterStatus(),
      ]);
      if (cancelled) return;
      setScannerConnected(scanner.connected);
      setPrinterConnected(printer.connected);
    }
    refreshDevices();
    const timer = window.setInterval(refreshDevices, 8000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    const stop = subscribeIdScanner((scanner) => {
      if (!scanner.openCheckIn || !scanner.lastScan || scanner.lastScan.id === openedScanId.current) return;
      openedScanId.current = scanner.lastScan.id;
      acknowledgeVisitorScan();
      setCheckInScanId(scanner.lastScan.id);
      setCheckInOpen(true);
    });
    prepareIdScanner();
    return stop;
  }, []);

  useEffect(() => () => {
    if (imageUrlRef.current) URL.revokeObjectURL(imageUrlRef.current);
  }, []);

  useEffect(() => {
    if (selectVisitId) openDetail(selectVisitId);
  }, [selectVisitId]);

  async function openVisitorId(visit) {
    if (!visit?.has_id_image || !visit.visitor_id || idLoadingId) return;
    setIdLoadingId(visit.id);
    setError('');
    try {
      const url = await fetchPrivateImage(`/visitors/people/${visit.visitor_id}/id-image?school_id=${schoolId}`, token);
      if (!url) {
        setError('The ID image could not be loaded.');
        return;
      }
      closeIdPreview();
      setIdPreview({ src: url, name: visit.full_name });
    } catch (idError) {
      setError(idError.message || 'The ID image could not be loaded.');
    } finally {
      setIdLoadingId(null);
    }
  }

  async function openDetail(visitId) {
    setError('');
    try {
      const response = await apiFetch(`/visitors/visits/${visitId}?school_id=${schoolId}`, {}, token);
      setDetail(response.data);
      if (imageUrlRef.current) URL.revokeObjectURL(imageUrlRef.current);
      imageUrlRef.current = '';
      setDetailImageUrl('');
      if (response.data.visit.has_id_image) {
        const url = await fetchPrivateImage(`/visitors/people/${response.data.visit.visitor_id}/id-image?school_id=${schoolId}`, token);
        imageUrlRef.current = url;
        setDetailImageUrl(url);
      }
    } catch (detailError) {
      setError(detailError.message || 'Visit details could not be loaded.');
    }
  }

  async function reprint(visit) {
    if (printingId) return;
    setPrintingId(visit.id);
    setError('');
    setNotice('');
    try {
      const response = await apiFetch(`/visitors/visits/${visit.id}?school_id=${schoolId}`, {}, token);
      if (response.data.screening_block) {
        throw new Error(response.data.screening_block);
      }
      const portraitDataUrl = await portraitForReprint(response.data.visit, token, schoolId);
      const printResult = await printVisitorBadge({
        ...response.data.badge,
        portraitDataUrl,
      });
      const recorded = await apiFetch(`/visitors/visits/${visit.id}/print-result?school_id=${schoolId}`, {
        method: 'POST',
        body: JSON.stringify({
          school_id: Number(schoolId),
          available: printResult.available,
          accepted: printResult.accepted,
          confirmed: printResult.confirmed,
          message: printResult.message,
        }),
      }, token);
      if (recorded.data.print.status === 'failed' || recorded.data.print.status === 'unavailable') {
        setError(recorded.data.print.message);
      }
      if (detail?.visit?.id === visit.id) await openDetail(visit.id);
    } catch (printError) {
      setError(printError.message || 'The badge could not be printed.');
    } finally {
      setPrintingId(null);
    }
  }

  async function confirmCheckout() {
    if (!checkoutVisit || checkingOut) return;
    setCheckingOut(true);
    setError('');
    try {
      await apiFetch(`/visitors/visits/${checkoutVisit.id}/checkout?school_id=${schoolId}`, {
        method: 'POST',
        body: JSON.stringify({ school_id: Number(schoolId) }),
      }, token);
      setNotice(`${checkoutVisit.full_name} is checked out.`);
      setCheckoutVisit(null);
      if (detail?.visit?.id === checkoutVisit.id) setDetail(null);
      await refresh();
    } catch (checkoutError) {
      setError(checkoutError.message || 'Checkout could not be saved.');
    } finally {
      setCheckingOut(false);
    }
  }

  async function deleteIdImage(visitorId) {
    setError('');
    try {
      await apiFetch(`/visitors/people/${visitorId}/id-image?school_id=${schoolId}`, { method: 'DELETE' }, token);
      if (imageUrlRef.current) URL.revokeObjectURL(imageUrlRef.current);
      imageUrlRef.current = '';
      setDetailImageUrl('');
      setNotice('ID image deleted.');
      if (detail?.visit?.id) await openDetail(detail.visit.id);
    } catch (deleteError) {
      setError(deleteError.message || 'The ID image could not be deleted.');
    }
  }

  function flagVisitor(visit) {
    setRestrictionForm({
      ...emptyRestriction(),
      full_name: visit.full_name,
      visitor_id: visit.visitor_id,
    });
    setRestrictionOpen(true);
    setTab('flagged');
    setDetail(null);
  }

  async function saveRestriction(event) {
    event.preventDefault();
    if (savingRestriction) return;
    setSavingRestriction(true);
    setError('');
    try {
      const payload = {
        school_id: Number(schoolId),
        full_name: restrictionForm.full_name,
        reason: restrictionForm.reason,
        severity: restrictionForm.severity,
        expires_on: restrictionForm.expires_on || null,
        visitor_id: restrictionForm.visitor_id,
      };
      if (restrictionForm.id) {
        await apiFetch(`/visitors/restrictions/${restrictionForm.id}?school_id=${schoolId}`, {
          method: 'PATCH',
          body: JSON.stringify(payload),
        }, token);
      } else {
        await apiFetch('/visitors/restrictions', {
          method: 'POST',
          body: JSON.stringify(payload),
        }, token);
      }
      setRestrictionForm(emptyRestriction());
      setRestrictionOpen(false);
      setNotice('Restriction saved.');
      await loadRestrictions();
    } catch (saveError) {
      setError(saveError.message || 'The restriction could not be saved.');
    } finally {
      setSavingRestriction(false);
    }
  }

  async function deactivateRestriction(restriction) {
    setError('');
    try {
      await apiFetch(`/visitors/restrictions/${restriction.id}/deactivate?school_id=${schoolId}`, {
        method: 'POST',
        body: JSON.stringify({ school_id: Number(schoolId) }),
      }, token);
      setNotice('Restriction deactivated.');
      await loadRestrictions();
    } catch (deactivateError) {
      setError(deactivateError.message || 'The restriction could not be deactivated.');
    }
  }

  function restrictionStatus(restriction) {
    if (!restriction.is_active) return 'Inactive';
    if (restriction.expires_at && new Date(restriction.expires_at).getTime() <= Date.now()) return 'Expired';
    return 'Active';
  }

  const printingName = visits.find((visit) => visit.id === printingId)?.full_name;

  function deviceState(connected) {
    if (connected === null) return 'Checking';
    return connected ? 'Connected' : 'Not connected';
  }

  async function testPrint() {
    if (testingPrint || printingId) return;
    setTestingPrint(true);
    setError('');
    setNotice('');
    try {
      const now = new Date();
      const result = await printVisitorBadge({
        schoolName: summary.school?.name || 'School',
        productName: 'Car-Pool',
        visitorTypeLabel: 'Test',
        fullName: 'Printer test',
        destination: 'Front desk',
        visitReasonLabel: 'Test print',
        dateLabel: now.toLocaleDateString([], { month: 'short', day: '2-digit', year: 'numeric' }),
        timeLabel: now.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }),
        badgeNumber: 'TEST',
      });
      if (!result?.accepted) {
        throw new Error(result?.message || 'The test badge was not printed.');
      }
      setNotice(result.message || 'Test badge sent to the LabelWriter.');
    } catch (printError) {
      setError(printError.message || 'The test badge was not printed.');
    } finally {
      setTestingPrint(false);
    }
  }

  return (
    <div className="vis-page">
      <div className="vis-devices" role="status" aria-live="polite">
        <div className={`vis-device${scannerConnected === null ? '' : scannerConnected ? ' is-on' : ' is-off'}`}>
          <span className="vis-device-dot" />
          <span className="vis-device-label">ID scanner</span>
          <strong>{deviceState(scannerConnected)}</strong>
        </div>
        <div className="vis-device-pair">
          <div className={`vis-device${printerConnected === null ? '' : printerConnected ? ' is-on' : ' is-off'}`}>
            <span className="vis-device-dot" />
            <span className="vis-device-label">Badge printer</span>
            <strong>{deviceState(printerConnected)}</strong>
          </div>
          <button
            type="button"
            className="sa-btn sa-btn-secondary vis-device-test"
            onClick={testPrint}
            disabled={testingPrint || Boolean(printingId) || printerConnected === false}
          >
            {testingPrint ? 'Printing…' : 'Test print'}
          </button>
        </div>
      </div>
      <div className="vis-heading">
        <div>
          <h1>Visitors</h1>
          <p>Check visitors in, print a badge, and see who is still on campus.</p>
        </div>
        <button
          ref={checkInButtonRef}
          type="button"
          className="sa-btn sa-btn-primary vis-checkin-btn"
          onClick={() => {
            setCheckInScanId(null);
            setCheckInOpen(true);
            prepareIdScanner();
          }}
        >
          Check In Visitor
        </button>
      </div>

      <div className="vis-stats">
        <div>
          <strong>{summary.on_campus || 0}</strong>
          <span>On campus</span>
        </div>
        <div>
          <strong>{summary.today_visits || 0}</strong>
          <span>Today’s visits</span>
        </div>
      </div>

      <div className="vis-tabs" role="tablist">
        <button type="button" className={tab === 'campus' ? 'active' : ''} onClick={() => setTab('campus')}>Currently on Campus</button>
        <button type="button" className={tab === 'history' ? 'active' : ''} onClick={() => setTab('history')}>Visit History</button>
        <button type="button" className={tab === 'flagged' ? 'active' : ''} onClick={() => setTab('flagged')}>Flagged People</button>
      </div>

      {error ? <div className="vis-alert vis-alert-block" role="alert">{error}</div> : null}
      {notice ? <div className="vis-alert vis-alert-ok" role="status">{notice}</div> : null}
      {printingId ? (
        <div className="vis-print-status" role="status">
          Printing badge{printingName ? ` for ${printingName}` : ''}…
        </div>
      ) : null}

      {tab === 'flagged' ? (
        <section className="vis-panel">
          <div className="vis-panel-heading">
            <h2>School restrictions</h2>
            <button type="button" className="sa-btn sa-btn-primary" onClick={() => { setRestrictionForm(emptyRestriction()); setRestrictionOpen(true); }}>
              Add restriction
            </button>
          </div>
          <p className="vis-help">These are entered by the school. Scanning an ID does not run a background check.</p>
          {restrictionOpen ? (
            <form className="vis-restriction-form" onSubmit={saveRestriction}>
              <label>
                Name
                <input value={restrictionForm.full_name} onChange={(event) => setRestrictionForm((current) => ({ ...current, full_name: event.target.value }))} required />
              </label>
              <label>
                Reason
                <input value={restrictionForm.reason} onChange={(event) => setRestrictionForm((current) => ({ ...current, reason: event.target.value }))} required />
              </label>
              <label>
                Severity
                <select value={restrictionForm.severity} onChange={(event) => setRestrictionForm((current) => ({ ...current, severity: event.target.value }))}>
                  {RESTRICTION_SEVERITIES.map((severity) => <option key={severity.value} value={severity.value}>{severity.label}</option>)}
                </select>
              </label>
              <label>
                Expiration date
                <input type="date" value={restrictionForm.expires_on} onChange={(event) => setRestrictionForm((current) => ({ ...current, expires_on: event.target.value }))} />
              </label>
              <div className="vis-form-actions">
                <button type="submit" className="sa-btn sa-btn-primary" disabled={savingRestriction}>
                  {savingRestriction ? 'Saving…' : 'Save restriction'}
                </button>
                <button type="button" className="sa-btn sa-btn-secondary" onClick={() => setRestrictionOpen(false)}>Cancel</button>
              </div>
            </form>
          ) : null}
          {loading ? <div className="sa-empty">Loading restrictions…</div> : restrictions.length ? (
            <ul className="vis-flag-list">
              {restrictions.map((restriction) => {
                const idImage = restrictionImages[restriction.id];
                const birthDate = formatCalendarDate(restriction.birth_date);
                return (
                  <li key={restriction.id} className="vis-flag-card">
                    {idImage ? (
                      <button
                        type="button"
                        className="vis-flag-id-btn"
                        aria-label={`View ID for ${restriction.full_name}`}
                        onClick={() => setIdPreview({ src: idImage, name: restriction.full_name })}
                      >
                        <img className="vis-flag-id" src={idImage} alt="" />
                      </button>
                    ) : null}
                    <div className="vis-flag-copy">
                      <div className="vis-flag-title">
                        <h3>{restriction.full_name}</h3>
                        <span className={`vis-status ${restrictionStatus(restriction) === 'Active' ? 'on-campus' : 'expired'}`}>
                          {restrictionStatus(restriction)}
                        </span>
                      </div>
                      <dl className="vis-flag-facts">
                        {birthDate ? (
                          <div>
                            <dt>Date of birth</dt>
                            <dd>{birthDate}</dd>
                          </div>
                        ) : null}
                        <div>
                          <dt>Reason</dt>
                          <dd>{restriction.reason}</dd>
                        </div>
                        <div>
                          <dt>Severity</dt>
                          <dd>{severityLabel(restriction.severity)}</dd>
                        </div>
                        <div>
                          <dt>Created</dt>
                          <dd>{formatVisitWhen(restriction.created_at)}</dd>
                        </div>
                        <div>
                          <dt>Staff</dt>
                          <dd>{restriction.created_by_name || '—'}</dd>
                        </div>
                        <div>
                          <dt>Expiration</dt>
                          <dd>{formatCalendarDate(restriction.expires_on) || 'None'}</dd>
                        </div>
                      </dl>
                    </div>
                    <div className="vis-actions">
                      <button
                        type="button"
                        className="sa-btn sa-btn-secondary"
                        onClick={() => {
                          setRestrictionForm({
                            id: restriction.id,
                            full_name: restriction.full_name,
                            reason: restriction.reason,
                            severity: restriction.severity,
                            expires_on: restriction.expires_on || '',
                            visitor_id: restriction.visitor_id,
                          });
                          setRestrictionOpen(true);
                        }}
                      >
                        Edit
                      </button>
                      {restriction.is_active ? (
                        <button type="button" className="sa-btn sa-btn-secondary" onClick={() => deactivateRestriction(restriction)}>
                          Deactivate
                        </button>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : <div className="sa-empty">No restrictions yet.</div>}
        </section>
      ) : (
        <section className="vis-panel">
          <div className="vis-filters">
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search by name"
              aria-label="Search by name"
            />
            {tab === 'history' ? (
              <input type="date" value={date} onChange={(event) => setDate(event.target.value)} aria-label="Visit date" />
            ) : null}
            <select value={visitorType} onChange={(event) => setVisitorType(event.target.value)} aria-label="Visitor type">
              <option value="">All visitor types</option>
              {VISITOR_TYPES.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}
            </select>
            {tab === 'history' ? (
              <select value={status} onChange={(event) => setStatus(event.target.value)} aria-label="Visit status">
                <option value="">All statuses</option>
                <option value="on_campus">On campus</option>
                <option value="checked_out">Checked out</option>
              </select>
            ) : null}
          </div>
          {loading ? <div className="sa-empty">Loading visits…</div> : visits.length ? (
            <div className="vis-table-wrap">
              <table className="sa-table">
                <thead>
                  <tr>
                    <th>Full name</th>
                    <th>Visitor type</th>
                    <th>Visit reason</th>
                    <th>Visiting / destination</th>
                    <th>Check-in</th>
                    <th>Checkout</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visits.map((visit) => (
                    <tr key={visit.id} className={detail?.visit?.id === visit.id ? 'vis-row-selected' : ''}>
                      <td>
                        <div className="vis-name-cell">
                          <span>{visit.full_name}</span>
                          {visit.has_id_image ? (
                            <button
                              type="button"
                              className="vis-id-icon"
                              aria-label={`View ID for ${visit.full_name}`}
                              disabled={idLoadingId === visit.id}
                              onClick={() => openVisitorId(visit)}
                            >
                              <IdCardIcon />
                            </button>
                          ) : null}
                        </div>
                      </td>
                      <td>{visitorTypeLabel(visit.visitor_type)}</td>
                      <td>{visitReasonLabel(visit.visit_reason)}</td>
                      <td>{visit.destination_label}</td>
                      <td>{formatVisitWhen(visit.checked_in_at)}</td>
                      <td>{formatVisitWhen(visit.checked_out_at)}</td>
                      <td>
                        <span className={`vis-status ${visit.status === 'on_campus' ? 'on-campus' : visit.checked_out_by_staff_id ? 'checked-out' : 'expired'}`}>
                          {visit.status === 'on_campus' ? 'On campus' : visit.checked_out_by_staff_id ? 'Checked out' : 'Expired'}
                        </span>
                      </td>
                      <td className="vis-actions">
                        <button type="button" className="sa-btn sa-btn-secondary" onClick={() => openDetail(visit.id)}>View Details</button>
                        {visit.status === 'on_campus' ? (
                          <button type="button" className="sa-btn sa-btn-secondary" onClick={() => setCheckoutVisit(visit)}>Check Out</button>
                        ) : null}
                        <button type="button" className="sa-btn sa-btn-secondary" disabled={printingId === visit.id} onClick={() => reprint(visit)}>
                          {printingId === visit.id ? 'Printing…' : 'Reprint Badge'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <div className="sa-empty">{tab === 'campus' ? 'Nobody is checked in right now.' : 'No visits match these filters.'}</div>}
        </section>
      )}

      {detail ? (
        <div className="vis-modal-overlay" onMouseDown={() => setDetail(null)}>
          <aside className="vis-detail" aria-label="Visit details" onMouseDown={(event) => event.stopPropagation()}>
            <div className="vis-panel-heading">
              <h2>{detail.visit.full_name}</h2>
              <button type="button" className="sa-btn sa-btn-secondary" onClick={() => setDetail(null)}>Close</button>
            </div>
            <dl className="vis-facts">
              <div><dt>Type</dt><dd>{visitorTypeLabel(detail.visit.visitor_type)}</dd></div>
              <div><dt>Reason</dt><dd>{visitReasonLabel(detail.visit.visit_reason)}</dd></div>
              <div><dt>Visiting</dt><dd>{detail.visit.destination_label}</dd></div>
              <div><dt>Badge</dt><dd>{detail.visit.badge_number}</dd></div>
              <div><dt>Checked in</dt><dd>{formatVisitWhen(detail.visit.checked_in_at)}{detail.visit.checked_in_by_name ? ` · ${detail.visit.checked_in_by_name}` : ''}</dd></div>
              <div>
                <dt>{detail.visit.checked_out_by_staff_id || !detail.visit.checked_out_at ? 'Checked out' : 'Expired'}</dt>
                <dd>
                  {detail.visit.checked_out_at
                    ? `${formatVisitWhen(detail.visit.checked_out_at)}${detail.visit.checked_out_by_name ? ` · ${detail.visit.checked_out_by_name}` : ''}`
                    : 'Still on campus'}
                </dd>
              </div>
            </dl>
            {detail.visit.company ? <p>Company: {detail.visit.company}</p> : null}
            {detail.visit.notes ? <p>Notes: {detail.visit.notes}</p> : null}
            {detailImageUrl ? <img className="vis-id-preview" src={detailImageUrl} alt="Stored ID" /> : null}
            <div className="vis-detail-actions">
              {detail.visit.has_id_image ? (
                <button type="button" className="sa-btn sa-btn-secondary" onClick={() => deleteIdImage(detail.visit.visitor_id)}>Delete ID image</button>
              ) : null}
              <button type="button" className="sa-btn sa-btn-secondary" onClick={() => flagVisitor(detail.visit)}>Add restriction</button>
            </div>
            <h3>Past visits</h3>
            <ul className="vis-past">
              {detail.past_visits.map((visit) => (
                <li key={visit.id}>
                  {formatVisitWhen(visit.checked_in_at)} · {visitReasonLabel(visit.visit_reason)} · {visit.destination_label}
                </li>
              ))}
            </ul>
          </aside>
        </div>
      ) : null}

      {idPreview ? (
        <div
          className="vis-id-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={`ID for ${idPreview.name}`}
          onMouseDown={closeIdPreview}
        >
          <button
            type="button"
            className="vis-id-lightbox-close"
            autoFocus
            onMouseDown={(event) => event.stopPropagation()}
            onClick={closeIdPreview}
          >
            Close
          </button>
          <img
            src={idPreview.src}
            alt={`ID for ${idPreview.name}`}
            onMouseDown={(event) => event.stopPropagation()}
          />
        </div>
      ) : null}

      {checkoutVisit ? (
        <div className="vis-modal-overlay">
          <div className="vis-confirm" role="dialog" aria-modal="true" aria-labelledby="visitor-checkout-title">
            <h2 id="visitor-checkout-title">Check out {checkoutVisit.full_name}?</h2>
            <p>The visit stays in today’s history.</p>
            <div className="vis-confirm-actions">
              <button type="button" className="sa-btn sa-btn-secondary" onClick={() => setCheckoutVisit(null)} disabled={checkingOut}>Cancel</button>
              <button type="button" className="sa-btn sa-btn-primary" disabled={checkingOut} onClick={confirmCheckout}>
                {checkingOut ? 'Checking out…' : 'Check Out'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <VisitorCheckInModal
        open={checkInOpen}
        schoolId={schoolId}
        schoolName={summary.school?.name || 'School'}
        token={token}
        scanId={checkInScanId}
        onClose={() => {
          setCheckInOpen(false);
          setCheckInScanId(null);
          window.setTimeout(() => checkInButtonRef.current?.focus(), 0);
        }}
        onComplete={refresh}
      />
    </div>
  );
}
